// 조용한 온천 배치: 탕 8곳의 위치·모양, 탕 안 자리, 큰 탕 가장자리 자리, 장애물. 프로토타입 255–287.
// three.js를 쓰지 않는 순수 TS: 자리 차지·정원은 나중에 방 서버가 판정하므로 packages/shared로 옮길 수 있게 둔다.
import { rnd } from '../../engine/math';

/** 탕 정면(+z 쪽) 방향 각도. */
export const FRONT = Math.PI / 2;

export type PoolName = 'big' | 'mid' | 'solo' | 'hidden';

export interface Pool {
  name: PoolName;
  kind: 'rock' | 'hinoki' | 'barrel';
  water: 'milky' | 'clear';
  x: number;
  z: number;
  /** 기본 반지름. 실제 테두리는 rAt(각도). */
  r: number;
  square?: boolean;
  /** 가장자리 굴곡 [시드, 세기] (바위 탕). */
  blob?: [number, number];
  /** 폭포가 떨어지는 탕. */
  fall?: boolean;
  yuzu: number;
  oke: number;
  steam: number;
  /** 각도 a 방향의 테두리 반지름. */
  rAt(a: number): number;
  /** 바위 사이로 드나드는 틈의 각도. */
  gaps: number[];
  slots: Slot[];
  /** 숨은 탕으로 가는 길의 대기 벤치 자리 2곳 (장면을 만들 때 채운다). */
  wait?: WaitSpot[];
}

export interface WaitSpot { x: number; z: number; face: number; occ: unknown; since: number }

export interface Slot {
  pool: Pool;
  x: number;
  z: number;
  /** 앉아서 바라보는 방향. */
  face: number;
  ang: number;
  /** 차지한 카피바라 (없으면 null). */
  occ: unknown;
  /** 큰 탕 가장자리 걸터앉는 자리. */
  seat?: boolean;
}

/** 원 장애물 또는 사각형 장애물 (보행 격자에서 막힌다). */
export type Obstacle = { rect?: false; x: number; z: number; r: number } | { rect: true; x: number; z: number; hw: number; hd: number };

const blob = (sd: number, amp: number) => {
  const k1 = rnd(sd) * 6.28, k2 = rnd(sd + 1) * 6.28;
  return (a: number) => 1 + amp * (0.6 * Math.sin(2 * a + k1) + 0.4 * Math.sin(3 * a + k2));
};

type PoolDef = Omit<Pool, 'rAt' | 'gaps' | 'slots' | 'wait'>;
const DEFS: PoolDef[] = [
  { name: 'big', kind: 'rock', water: 'milky', x: 0, z: -2, r: 4.0, blob: [11, 0.07], yuzu: 22, oke: 1, steam: 28 },
  { name: 'mid', kind: 'rock', water: 'milky', x: -12, z: -5.5, r: 2.4, blob: [21, 0.08], fall: true, yuzu: 9, oke: 1, steam: 12 },
  { name: 'mid', kind: 'rock', water: 'milky', x: 12, z: -5.5, r: 2.4, blob: [31, 0.08], fall: true, yuzu: 9, oke: 0, steam: 12 },
  { name: 'solo', kind: 'hinoki', water: 'clear', x: -15.5, z: 1.5, r: 1.15, square: true, yuzu: 0, oke: 1, steam: 5 },
  { name: 'solo', kind: 'hinoki', water: 'clear', x: -15.5, z: 7.8, r: 1.15, square: true, yuzu: 0, oke: 1, steam: 5 },
  { name: 'solo', kind: 'hinoki', water: 'clear', x: 15.5, z: 4.5, r: 1.15, square: true, yuzu: 0, oke: 1, steam: 5 },
  { name: 'hidden', kind: 'rock', water: 'clear', x: -19, z: -19, r: 1.45, blob: [41, 0.1], yuzu: 3, oke: 1, steam: 6 },
  { name: 'hidden', kind: 'barrel', water: 'clear', x: 19, z: -19, r: 1.3, yuzu: 3, oke: 0, steam: 6 },
];

export interface QuietLayout {
  pools: Pool[];
  /** 큰 탕 (pools[0]). */
  big: Pool;
  /** 큰 탕 가장자리 걸터앉는 자리 4곳. */
  seats: Slot[];
  /** 장면을 만들며 채우는 장애물 (나무, 건물, 울타리 등). */
  obstacles: Obstacle[];
}

export function makeLayout(): QuietLayout {
  const pools: Pool[] = DEFS.map((d) => ({ ...d, rAt: () => d.r, gaps: [], slots: [] }));
  const big = pools[0]!;
  for (const P of pools) {
    const shape = P.blob ? blob(P.blob[0], P.blob[1]) : null;
    P.rAt = P.square ? (a) => P.r / Math.max(Math.abs(Math.cos(a)), Math.abs(Math.sin(a))) : shape ? (a) => P.r * shape(a) : () => P.r;
    P.gaps = P.name === 'big' ? [FRONT, FRONT + 2.1, FRONT - 2.1] : [Math.atan2(big.z - P.z, big.x - P.x)];
  }
  pools.forEach((P, pi) => {
    const add = (rad: number, ang: number, face?: number) => P.slots.push({ pool: P, x: P.x + Math.cos(ang) * rad, z: P.z + Math.sin(ang) * rad, face: face != null ? face : (rnd(pi * 31 + P.slots.length) - 0.5) * 1.0, occ: null, ang });
    if (P.name === 'big') { for (let i = 0; i < 4; i++) add(1.3, i / 4 * Math.PI * 2 + 0.4); for (let i = 0; i < 8; i++) add(2.75, i / 8 * Math.PI * 2 + 0.2); }
    else if (P.name === 'mid') { add(0, 0); for (let i = 0; i < 4; i++) add(1.25, i / 4 * Math.PI * 2 + 0.78); }
    else if (P.name === 'hidden') { const g = P.gaps[0]!; add(0, 0, Math.atan2(Math.cos(g), Math.sin(g))); P.wait = []; }
    else add(0, 0, (rnd(pi) - 0.5) * 0.4);
  });
  const seats: Slot[] = [FRONT + 1.05 - 0.35, FRONT + 1.05 + 0.35, FRONT - 1.05 - 0.35, FRONT - 1.05 + 0.35].map((a) => {
    const R = big.rAt(a) + 0.95;
    return { pool: big, seat: true, x: big.x + Math.cos(a) * R, z: big.z + Math.sin(a) * R, ang: a, face: 0, occ: null };
  });
  return { pools, big, seats, obstacles: [] };
}

/** (x, z)가 탕 안인지. m만큼 테두리를 넓혀 본다. */
export const inPool = (P: Pool, x: number, z: number, m = 0): boolean =>
  Math.hypot(x - P.x, z - P.z) < P.rAt(Math.atan2(z - P.z, x - P.x)) + m;

/** 사각형 장애물 안인지 (m만큼 넓혀서). */
export const inRect = (o: { x: number; z: number; hw: number; hd: number }, x: number, z: number, m: number): boolean =>
  Math.abs(x - o.x) < o.hw + m && Math.abs(z - o.z) < o.hd + m;
